import { useMemo, useRef, useState } from "react";
import { startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks } from "date-fns";
import { Share, ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { Routine } from "instantdb/types";
import { hasDayCompleted, isRoutineActiveForDate } from "@/hooks/routines/useRoutine.utils";
import { extractDateFromDate } from "@/utilities/dateFormatter";
import { useUser } from "@/hooks/useUser";
import { useTheme } from "@/components/themeProvider";
import { useTranslation } from "@/hooks/useTranslation";
import { updateDayCompleted, getRoutinesOnce } from "instantdb/client/routine";
import { LoadingButton } from "@/components/ui/loading-button";
import { calculateWeeklyStats } from "@/utilities/weeklyStats";
import { NewResourceCard } from "@/components/cards/NewResourceCard";
import { useRoutines } from "@/hooks/routines/useRoutines";
import { useConfettiProgress } from "@/hooks/useConfettiProgress";
import { Heading } from "@/components/ui/heading";

interface ShareProgressProps {
  routines: Routine[];
  currentDate: Date;
  onDateChange?: (date: Date) => void;
  isHomepage?: boolean;
  hideShareButton?: boolean;
  hideNavigation?: boolean;
}

export function ShareProgress({
  routines,
  currentDate,
  onDateChange,
  isHomepage = false,
  hideShareButton = false,
  hideNavigation = false,
}: ShareProgressProps) {
  const { user, userId } = useUser();
  const { theme } = useTheme();
  const { t, locale } = useTranslation();
  const shareRef = useRef<HTMLDivElement>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const { handleCreateRoutine } = useRoutines();
  const { checkAndFireConfetti } = useConfettiProgress();

  const weekStartsOn = (user?.weekStartsOn as 0 | 1) ?? 1;

  // Navigation functions
  const goToPreviousWeek = () => {
    onDateChange?.(subWeeks(currentDate, 1));
  };

  const goToNextWeek = () => {
    onDateChange?.(addWeeks(currentDate, 1));
  };

  const goToToday = () => {
    onDateChange?.(new Date());
  };

  // Check if current date is today - use date strings for reliable comparison
  const isToday = useMemo(() => {
    const currentDateCopy = new Date(currentDate);
    currentDateCopy.setHours(0, 0, 0, 0);
    const todayCopy = new Date();
    todayCopy.setHours(0, 0, 0, 0);
    return currentDateCopy.getTime() === todayCopy.getTime();
  }, [currentDate]);

  // Get the days of the selected week
  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn });
    const end = endOfWeek(currentDate, { weekStartsOn });
    return eachDayOfInterval({ start, end });
  }, [currentDate, weekStartsOn]);

  // Get active routines (those that have at least one day scheduled)
  const activeRoutines = useMemo(() => {
    return routines.filter((routine) => {
      if (routine.dailyRoutine) return true;
      return routine.selectedDays && routine.selectedDays.length > 0;
    });
  }, [routines]);

  // Generate completion data for the grid - simplified to only show completed/non-completed
  const completionData = useMemo(() => {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    return activeRoutines.map((routine) => {
      const weekData = weekDays.map((day) => {
        const dateString = extractDateFromDate(day);
        const isCompleted = isHomepage ? true : hasDayCompleted(routine, dateString); // Force completed for homepage
        const isActiveForDate = isRoutineActiveForDate(routine, dateString);

        // Reset time to start of day for accurate comparison
        const dayStart = new Date(day);
        dayStart.setHours(0, 0, 0, 0);
        const isFutureDate = dayStart > currentDate;

        return {
          date: day,
          dateString,
          isCompleted,
          isActiveForDate,
          isFutureDate,
        };
      });

      return {
        routine,
        weekData,
      };
    });
  }, [activeRoutines, weekDays, isHomepage]);

  // Calculate overall completion stats using the shared utility function
  const stats = useMemo(() => {
    return calculateWeeklyStats(routines, currentDate, weekStartsOn);
  }, [routines, currentDate, weekStartsOn]);

  const handleShare = async () => {
    if (!shareRef.current) return;

    setIsGeneratingImage(true);

    try {
      // Dynamic import to avoid SSR issues
      const html2canvas = (await import("html2canvas-pro")).default;

      const canvas = await html2canvas(shareRef.current, {
        backgroundColor: theme === "dark" ? "#0a0a0a" : "#ffffff",
        scale: 2,
        useCORS: true,
        allowTaint: true,
        width: shareRef.current.offsetWidth,
        height: shareRef.current.offsetHeight,
      });

      canvas.toBlob(async (blob: Blob | null) => {
        if (!blob) return;

        const file = new File([blob], "progress-share.png", { type: "image/png" });

        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          try {
            await navigator.share({
              title: t("share_progress.share_title"),
              text: t("share_progress.share_text", {
                percentage: stats.completionPercentage,
              }),
              files: [file],
            });
          } catch (error) {
            if (error instanceof Error && error.name !== "AbortError") {
              console.error("Share failed:", error);
              downloadImage(blob);
            }
          }
        } else {
          downloadImage(blob);
        }
      }, "image/png");
    } catch (error) {
      console.error("Failed to generate image:", error);
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const downloadImage = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "progress-share.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (activeRoutines.length === 0) {
    return (
      <div className="col-span-12 grid grid-cols-12 gap-4 @container">
        <Heading level="h2" size="h3" className="col-span-12 tracking-tight select-none w-full">
          {t("dashboard.routines")}
        </Heading>

        <NewResourceCard
          title={t("filtered_routines.no_active_routines")}
          description={t("filtered_routines.create_new_routine")}
          className="col-span-12 @[768px]:col-span-6 @[1024px]:col-span-4 @[1024px]:col-start-1"
          onClick={() => handleCreateRoutine({})}
        />
      </div>
    );
  }

  return (
    // col-span-12 @[768px]:col-span-6 @[1024px]:col-span-4
    <div
      className={
        isHomepage
          ? "flex w-full h-full justify-center items-center space-y-4"
          : "col-span-12 @[768px]:col-span-8 @[1024px]:col-span-6 @[768px]:col-start-1 @[1024px]:col-start-1 space-y-4"
      }
    >
      {!hideNavigation && (
        <div className="flex items-center justify-between space-x-2">
          <Heading level="h2" size="h3" className="tracking-tight select-none w-full">
            {t("dashboard.routines")}
          </Heading>

          {/* Week Navigation */}
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={goToPreviousWeek}
              className="h-8 w-8"
              aria-label="Previous week"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <Button
              variant={isToday ? "outline" : "default"}
              size="sm"
              onClick={goToToday}
              className="px-3"
              aria-label="Go to current week"
            >
              <Calendar className="h-4 w-4 mr-1" />
              {t("nav.today")}
            </Button>

            <Button
              variant="outline"
              size="icon"
              onClick={goToNextWeek}
              className="h-8 w-8"
              aria-label="Next week"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <div ref={shareRef} className="">
        {/* Progress grid */}
        <Card className="bg-white dark:bg-gray-950/50 border border-gray-200/60 dark:border-gray-800/60 shadow-sm overflow-hidden backdrop-blur-sm">
          <CardContent className="p-0">
            <table className="w-full table-fixed">
              <thead>
                <tr className="border-b border-gray-200/60 dark:border-gray-800/60">
                  <th className="text-left px-3 py-2 @[1024px]:px-6 @[1024px]:py-4 border-r border-gray-200/60 dark:border-gray-800/60 w-[120px] @[375px]:w-[130px] @[1024px]:w-[200px] min-w-[120px] @[375px]:min-w-[130px] @[1024px]:min-w-[200px] max-w-[130px] @[375px]:max-w-[140px] @[1024px]:max-w-[260px]">
                    {/* Empty header cell */}
                  </th>
                  {weekDays.map((day, index) => (
                    <th
                      key={day.toISOString()}
                      className={cn(
                        "text-center text-muted-foreground border-gray-200/60 dark:border-gray-800/60 px-1 py-2 @[1024px]:px-2 @[1024px]:py-4",
                        index < weekDays.length - 1 && "border-r",
                      )}
                    >
                      <div className="text-sm">
                        <span className="hidden @[576px]:inline capitalize">
                          {day.toLocaleDateString(locale, { weekday: "short" })}
                        </span>
                        <span className="@[576px]:hidden capitalize">
                          {day.toLocaleDateString(locale, { weekday: "short" })[0]}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {completionData.map(({ routine, weekData }, rowIndex) => (
                  <tr
                    key={routine.id}
                    className={cn(
                      "hover:bg-gray-50/50 dark:hover:bg-gray-900/30 transition-colors duration-200",
                      rowIndex < completionData.length - 1 &&
                        "border-b border-gray-200/40 dark:border-gray-800/40",
                    )}
                  >
                    {/* Routine name cell */}
                    <td className="px-3 py-2 @[576px]:px-4 @[576px]:py-4 border-r border-gray-200/60 dark:border-gray-800/60 w-[120px] @[375px]:w-[130px] @[576px]:w-[200px] min-w-[120px] @[375px]:min-w-[130px] @[576px]:min-w-[200px] max-w-[130px] @[375px]:max-w-[140px] @[576px]:max-w-[260px]">
                      <div className="flex items-center gap-2 @[576px]:gap-3 text-xs @[576px]:text-sm font-medium">
                        <span
                          className="leading-snug font-medium line-clamp-2"
                          title={routine.title}
                        >
                          {routine.symbol || "📋"} {routine.title}
                        </span>
                      </div>
                    </td>

                    {/* Completion cells */}
                    {weekData.map(
                      ({ date, isCompleted, isActiveForDate, isFutureDate }, cellIndex) => (
                        <td
                          key={date.toISOString()}
                          className={cn(
                            "text-center py-2 px-1 @[576px]:py-4 @[576px]:px-2 border-gray-200/40 dark:border-gray-800/40",
                            cellIndex < weekData.length - 1 && "border-r",
                          )}
                        >
                          <div className="flex items-center justify-center">
                            {isActiveForDate && (
                              <Checkbox
                                checked={isCompleted}
                                variant="routine"
                                routineColor={routine.color}
                                theme={theme as "light" | "dark"}
                                disabled={isFutureDate}
                                onCheckedChange={async (checked) => {
                                  if (!isFutureDate && userId) {
                                    const done = checked === true;
                                    const targetDate = extractDateFromDate(date);

                                    try {
                                      // Get routine step IDs
                                      const routineStepIds = (routine.steps ?? [])
                                        .map((step) => step.id)
                                        .filter((id): id is string => id != null);

                                      // For routines with no steps, we only need to set the completed flag
                                      if (routine.steps?.length === 0) {
                                        await updateDayCompleted(
                                          routine.id,
                                          userId,
                                          targetDate,
                                          [],
                                          done,
                                        );
                                      } else {
                                        // For routines with steps, mark all steps as completed/incomplete
                                        const stepsCompleted = done ? routineStepIds : [];
                                        await updateDayCompleted(
                                          routine.id,
                                          userId,
                                          targetDate,
                                          stepsCompleted,
                                          done,
                                        );
                                      }

                                      // Check for confetti if marking as done and it's today
                                      if (done && targetDate === extractDateFromDate(new Date())) {
                                        const { routines: allRoutines } =
                                          await getRoutinesOnce(userId);
                                        checkAndFireConfetti(allRoutines);
                                      }

                                      if (done && window.umami) {
                                        window.umami.track("share-progress-mark-day");
                                      }
                                    } catch (error) {
                                      console.error("Failed to update routine completion:", error);
                                    }
                                  }
                                }}
                              />
                            )}
                          </div>
                        </td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {!hideShareButton && (
        <div className="flex justify-center">
          <LoadingButton
            variant="link"
            size="icon"
            onClick={handleShare}
            disabled={isGeneratingImage}
            loading={isGeneratingImage}
            className=" mx-auto"
          >
            {!isGeneratingImage && <Share />}
            {t("share_progress.share_button")}
          </LoadingButton>
        </div>
      )}
    </div>
  );
}
